import { useContext, useEffect, useState } from "react";
import AuthContext from "../../context/AuthContext";
import useSnackBar from "../../hooks/UseSnackBar";
import { useWaits } from "../../hooks/UseWait";
import { Avatar, Box, Button, Chip, CircularProgress, Paper, Table, TableBody, TableContainer, TableHead, TableRow, Typography, useTheme } from "@mui/material";
import Fetch from "../../services/Fetch";
import SnackbarAlert from "../../components/SnackBar";
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import AddIcon from '@mui/icons-material/Add';
import { useConstants } from "../../hooks/UseConstants";
import { useTableStyles } from "../../hooks/UseTableStyles";
import { usePopups } from "../../hooks/UsePopups";
import AddCategory from "../../popup/AddCategory";
import UpdateCategory from "../../popup/UpdateCategory";
import CategoryDetails from "../../popup/CategoryDetails";
import DeleteDialog from "../../popup/DeleteDialog";
import { FormattedMessage, useIntl } from "react-intl";
import { usePagination } from "../../hooks/UsePagination";
import { useSearch } from "../../hooks/UseSearch";
import AdminPageHero from "../../components/AdminPageHero";
import DashboardPanelCard from "../../components/cards/DashboardPanelCard";
import PageContent from "../../components/PageContent";
import ScrollReveal from "../../components/motion/ScrollReveal";
import ModalOverlay from "../../components/ModalOverlay";
import { parsePaginatedResponse } from "../../utils/parsePaginatedResponse";

function Categories() {
    const intl = useIntl();
    const theme = useTheme();
    const { wait } = useContext(AuthContext);
    const { setPopup } = usePopups();
    const { host, language } = useConstants();
    const { StyledTableCell, StyledTableRow } = useTableStyles();
    const { search, setSearch } = useSearch();
    const { getWait, setGetWait } = useWaits();
    const { openSnackBar, type, message, setSnackBar, setOpenSnackBar } = useSnackBar();
    const { page, setPage, currentPage, setCurrentPage, totalPages, setTotalPages } = usePagination();
    const [categories, setCategories] = useState([]);
    const [categoriesCount, setCategoriesCount] = useState(0);
    const [category, setCategory] = useState('');
    const [categoryId, setCategoryId] = useState('');

    const getCategories = async () => {
        const params = new URLSearchParams({
            page: String(page + 1),
            per_page: '100',
        });
        if (search?.trim()) {
            params.set('search', search.trim());
        }

        const result = await Fetch(
            `${host}/categories?${params.toString()}`,
            'GET',
            null
        );

        if (result.status === 200) {
            const { items, pagination } = parsePaginatedResponse(result);
            setCategories(items);
            setCategoriesCount(pagination.total);
            setTotalPages(pagination.last_page);
            setCurrentPage(page);
        } else {
            setCategories([]);
        }

        setGetWait(false);
    };

    const updateCategory = (id) => {
        setCategory(categories.find((c) => c.id === id));
        setPopup('update', 'flex');
    };

    const categoryDetails = (id) => {
        setCategory(categories.find((c) => c.id === id));
        setPopup('details', 'flex');
    };

    const deleteCategory = async () => {
        const result = await Fetch(host + `/admin/categories/${categoryId}/delete`, 'DELETE', null);

        if (result.status === 200) {
            setCategories((list) => list.filter((c) => c.id !== categoryId));
            setSnackBar('success', <FormattedMessage id="category_deleted_msg" />);
            setPopup('delete', 'none');
        }
    };

    useEffect(() => {
        if (!wait) {
            setGetWait(true);
            getCategories();
        }
    }, [wait, page, search]);


    return (
        <>
            <PageContent wait={wait} loading={getWait && !wait}>
                <AdminPageHero
                                    actions={
                                        <Button variant="contained" onClick={() => setPopup('add', 'flex')} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.2)', '&:hover': { bgcolor: 'rgba(255,255,255,0.35)' } }}>
                                            <AddIcon fontSize="small" />
                                            <FormattedMessage id="add_category" />
                                        </Button>
                                    }
                />
                <ScrollReveal>
                <DashboardPanelCard className="mx-0 !mt-0">
                                <TableContainer component={Paper} dir={language === 'en' ? 'ltr' : "rtl"} sx={{ boxShadow: 'none' }}>
                                    <Box className="min-h-12 py-2 px-2 flex justify-between items-center max-sm:flex-col">
                                        <Box className="w-full flex items-center">
                                            <Box className="w-2/4 relative mr-3 max-sm:w-full">
                                                <input
                                                    style={{ backgroundColor: theme.palette.background.default }}
                                                    onChange={(e) => { setPage(0); setSearch(e.target.value); }}
                                                    className="w-10/12 h-12 rounded-md border indent-14 outline-none max-sm:w-full"
                                                    placeholder={intl.formatMessage({ id: 'search_categories' })}
                                                />
                                                <SearchOutlinedIcon className="absolute top-1/2 -translate-y-1/2 right-3 text-gray-500" sx={{ right: language === 'en' && '90%' }} />
                                            </Box>
                                        </Box>
                                        <Typography variant="body1" className="!text-gray-500 max-sm:mt-2">
                                            <FormattedMessage id="total_categories" />: {categoriesCount}
                                        </Typography>
                                    </Box>
                                    <Table sx={{ minWidth: 700 }} aria-label="categories table">
                                        <TableHead className="bg-gray-200">
                                            <TableRow sx={{ backgroundColor: theme.palette.background.paper }}>
                                                <StyledTableCell align={language === 'en' ? 'left' : 'right'}><FormattedMessage id="category_name" /></StyledTableCell>
                                                <StyledTableCell align={language === 'en' ? 'left' : 'right'}><FormattedMessage id="linked_majors" /></StyledTableCell>
                                                <StyledTableCell align={language === 'en' ? 'left' : 'right'} className="!text-center"><FormattedMessage id="procedures" /></StyledTableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {categories.map((item) => (
                                                <StyledTableRow
                                                    key={item.id}
                                                    className="hover:bg-gray-400 duration-100 cursor-pointer"
                                                    onClick={() => categoryDetails(item.id)}
                                                >
                                                    <StyledTableCell align={language === 'en' ? 'left' : 'right'} component="th" scope="row">
                                                        <Box className="flex items-center gap-3">
                                                            <Avatar src={item.image} alt="" variant="rounded" />
                                                            <Typography>{language === 'en' ? item.name_en : item.name_ar}</Typography>
                                                        </Box>
                                                    </StyledTableCell>
                                                    <StyledTableCell align={language === 'en' ? 'left' : 'right'}>
                                                        <Box className="flex flex-wrap gap-1">
                                                            {item.majors?.length ? item.majors.map((m) => (
                                                                <Chip key={m.id} size="small" label={language === 'en' ? m.name_en : m.name_ar} />
                                                            )) : (
                                                                <Typography variant="body2" className="text-gray-400">
                                                                    <FormattedMessage id="nothing" />
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                    </StyledTableCell>
                                                    <StyledTableCell align="right" className="!flex justify-around items-center" onClick={(e) => e.stopPropagation()}>
                                                        <Button
                                                            variant="contained"
                                                            className="!bg-red-300 !font-bold !text-red-700 hover:!bg-red-500 hover:!text-white duration-300"
                                                            onClick={() => { setCategoryId(item.id); setPopup('delete', 'flex'); }}
                                                        >
                                                            <FormattedMessage id="delete" />
                                                        </Button>
                                                        <Button
                                                            variant="contained"
                                                            className="!bg-green-300 !font-bold !text-green-800 hover:!bg-green-500 hover:!text-white duration-300"
                                                            onClick={() => updateCategory(item.id)}
                                                        >
                                                            <FormattedMessage id="update" />
                                                        </Button>
                                                    </StyledTableCell>
                                                </StyledTableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                    <Box className="flex justify-center items-center" dir="rtl">
                                        <Button disabled={page + 1 >= totalPages} onClick={() => setPage(currentPage + 1)}>
                                            <NavigateNextIcon fontSize="large" />
                                        </Button>
                                        <Typography variant="body1" className="!text-xl" dir="ltr">{currentPage + 1} / {totalPages}</Typography>
                                        <Button disabled={page === 0} onClick={() => setPage(currentPage - 1)}>
                                            <NavigateBeforeIcon fontSize="large" />
                                        </Button>
                                    </Box>
                                </TableContainer>
                </DashboardPanelCard>
                </ScrollReveal>
            </PageContent>

                    <ModalOverlay id="add" onClose={() => setPopup('add', 'none')}>
                        <AddCategory onClickClose={() => setPopup('add', 'none')} setSnackbar={setSnackBar} getCategories={getCategories} />
                    </ModalOverlay>

                    <ModalOverlay id="update" onClose={() => setPopup('update', 'none')}>
                        <UpdateCategory setSnackbar={setSnackBar} setCategory={setCategory} category={category} onClickClose={() => setPopup('update', 'none')} getCategories={getCategories} />
                    </ModalOverlay>

                    <ModalOverlay id="delete" onClose={() => setPopup('delete', 'none')}>
                        <DeleteDialog
                            onClickConfirm={deleteCategory}
                            onClickCancel={() => setPopup('delete', 'none')}
                            title={<FormattedMessage id="category_delete" />}
                            subtitle={<FormattedMessage id="category_delete_description" />}
                        />
                    </ModalOverlay>

                    <ModalOverlay id="details" onClose={() => setPopup('details', 'none')}>
                        <CategoryDetails
                            onClickClose={() => setPopup('details', 'none')}
                            setSnackBar={setSnackBar}
                            setCategories={setCategories}
                            category={category}
                            onClickUpdate={updateCategory}
                        />
                    </ModalOverlay>

            <SnackbarAlert open={openSnackBar} message={message} severity={type} onClose={() => setOpenSnackBar(false)} />
        </>
    );
}

export default Categories;
